import request from '@/utils/request';

/**
 * AI 模型管理相关 API
 */

/**
 * 获取模型列表（分页）
 * @param {Object} params - 请求参数
 * @param {number} params.pageNum - 页码
 * @param {number} params.pageSize - 每页大小
 * @param {string} [params.modelCode] - 模型编码（模糊搜索）
 * @param {string} [params.modelName] - 模型名称（模糊搜索）
 * @param {string} [params.modelType] - 模型类型筛选
 * @param {number} [params.status] - 启用状态 1-启用 0-禁用
 * @param {Array} [params.orderByList] - 排序列表
 * @returns {Promise} 返回模型列表数据
 */
export function getModelList(params) {
  const data = {
    pageNum: params.pageNum || 1,
    pageSize: params.pageSize || 10,
    modelCode: params.modelCode || '',
    modelName: params.modelName || '',
    modelType: params.modelType || '',
    modelFactory: params.modelFactory || '',
    status: params.status !== undefined && params.status !== null ? params.status : undefined,
    orderByList: params.orderByList || []
  };

  // 移除 undefined 的字段
  Object.keys(data).forEach(key => {
    if (data[key] === undefined) {
      delete data[key];
    }
  });

  return request({
    url: '/manager/aiModelConfig/page',
    method: 'post',
    data
  });
}

/**
 * 获取模型详情
 * @param {number|string} id - 模型 ID
 * @returns {Promise} 返回模型详情数据
 */
export function getModelDetail(id) {
  return request({
    url: '/manager/aiModelConfig/detail',
    method: 'post',
    data: { id }
  });
}

/**
 * 创建模型
 * @param {Object} data - 模型数据
 * @param {string} data.modelCode - 模型编码
 * @param {string} data.modelName - 模型名称
 * @param {string} [data.modelIcon] - 模型图标
 * @param {string} [data.modelDesc] - 模型描述
 * @param {string} data.modelType - 模型类型
 * @param {number} [data.hot=0] - 是否热门 1=是 0=否
 * @param {number} [data.free=0] - 是否免费 1=是 0=否
 * @param {number} [data.stream=1] - 是否流式输出 1=是 0=否
 * @param {number} [data.status=1] - 启用状态 1=启用 0=禁用
 * @param {number} [data.sortOrder=0] - 排序权重
 * @param {number} data.literaryScore - 文采水平评分 (1-5)
 * @param {number} data.instructionScore - 指令遵从评分 (1-5)
 * @param {number} data.tokenCostScore - 字数消耗评分 (1-5)
 * @param {string} data.backendModelName - 后端实际模型名
 * @param {string} [data.apiEndpoint] - 模型 API 地址
 * @param {string} [data.modelVersion] - 模型版本
 * @param {number} [data.maxContextLength] - 最大上下文长度 (K tokens)
 * @param {string} [data.notes] - 注意事项/使用说明
 * @param {string} [data.customTags] - 自定义标签 (JSON 字符串)
 * @param {number} [data.unitPrice] - 模型单价
 * @param {number} [data.dailyLimit] - 每日调用限额
 * @param {Array} [data.supportedLanguages] - 支持语言 (JSON 字符串)
 * @param {string} [data.scenarios] - 适用场景
 * @returns {Promise} 返回创建结果
 */
export function createModel(data) {
  return request({
    url: '/manager/aiModelConfig/create',
    method: 'post',
    data
  });
}

/**
 * 更新模型信息
 * @param {number|string} id - 模型 ID
 * @param {Object} data - 模型数据（同 createModel）
 * @returns {Promise} 返回更新结果
 */
export function updateModel(id, data) {
  return request({
    url: '/manager/aiModelConfig/update',
    method: 'post',
    data: { ...data, id }
  });
}

/**
 * 启用模型配置
 * @param {Object} data - 请求参数
 * @param {number|string} data.id - 模型 ID
 * @returns {Promise} 返回模型详情数据
 */
export function enableModel(data) {
  return request({
    url: '/manager/aiModelConfig/enable',
    method: 'post',
    data: { id: data.id }
  });
}

/**
 * 停用模型配置
 * @param {Object} data - 请求参数
 * @param {number|string} data.id - 模型 ID
 * @returns {Promise} 返回模型详情数据
 */
export function disableModel(data) {
  return request({
    url: '/manager/aiModelConfig/disable',
    method: 'post',
    data: { id: data.id }
  });
}

/**
 * 删除模型（逻辑删除）
 * @param {number|string} id - 模型 ID
 * @returns {Promise} 返回删除结果
 */
export function deleteModel(id) {
  return request({
    url: '/manager/aiModelConfig/delete',
    method: 'post',
    data: { id }
  });
}

/**
 * 获取所有可用模型（不分页，用于前端选择器）
 * @param {Object} params - 请求参数
 * @param {string} [params.modelType] - 模型类型筛选
 * @param {boolean} [params.onlyEnabled] - 是否只返回启用的模型
 * @returns {Promise} 返回模型列表
 */
export function getAllModels(params) {
  return request({
    url: '/user/ai-model/page',
    method: 'post',
    params
  });
}

/**
 * 查询用户侧启用的模型列表（不分页）
 * @returns {Promise} 返回启用的模型列表
 */
export function getEnabledModelList() {
  return request({
    url: '/user/ai-model/list',
    method: 'post'
  });
}

/**
 * 分页查询用户侧AI模型列表
 * @param {Object} params - 请求参数
 * @param {number} [params.pageNum] - 页码
 * @param {number} [params.pageSize] - 每页大小
 * @param {string} [params.modelName] - 模型名称（模糊搜索）
 * @param {Array} [params.orderByList] - 排序列表
 * @returns {Promise} 返回分页模型列表
 */
export function getModelPage(params) {
  return request({
    url: '/user/ai-model/page',
    method: 'post',
    data: params
  });
}

/**
 * 查询用户常用AI模型
 * @returns {Promise} 返回常用模型详情
 */
export function getCommonModel() {
  return request({
    url: '/user/ai-model/common',
    method: 'post'
  });
}

/**
 * 授权模型API
 * @param {Object} data - 请求参数
 * @param {number|string} data.id - 模型ID
 * @returns {Promise} 返回授权结果
 */
export function authorizeModelApi(data) {
  return request({
    url: '/manager/aiModelConfig/authorize',
    method: 'post',
    data: { id: data.id }
  });
}

/**
 * 获取模型厂商列表
 * @returns {Promise} 返回厂商列表 {key: label} 格式
 */
export function getModelFactoryList() {
  return request({
    url: '/dict/factory',
    method: 'post'
  });
}

/**
 * 获取模型列表（不分页，支持模糊搜索）
 * @param {Object} params - 请求参数
 * @param {string} [params.modelName] - 模型名称（模糊搜索，至少2个字符）
 * @param {string} [params.modelCode] - 模型编码（模糊搜索）
 * @param {string} [params.modelType] - 模型类型
 * @param {string} [params.modelFactory] - 模型厂商
 * @param {number} [params.status] - 启用状态 1-启用 0-禁用
 * @returns {Promise} 返回模型列表
 */
export function getModelListNoPage(params) {
  const data = {
    modelName: params.modelName || '',
    modelCode: params.modelCode || '',
    modelType: params.modelType || '',
    modelFactory: params.modelFactory || '',
    status: params.status !== undefined && params.status !== null ? params.status : undefined,
    orderByList: [{ field: 'sortOrder', operator: 'desc' }]
  };

  // 移除 undefined 的字段
  Object.keys(data).forEach(key => {
    if (data[key] === undefined) {
      delete data[key];
    }
  });

  return request({
    url: '/manager/aiModelConfig/list',
    method: 'post',
    data
  });
}

/**
 * 获取模型授权码列表（分页）
 * @param {Object} params - 请求参数
 * @param {number|string} params.modelId - 模型ID
 * @param {number} params.pageNum - 页码
 * @param {number} params.pageSize - 每页大小
 * @param {string} [params.apiKey] - API密钥（模糊搜索）
 * @param {number} [params.status] - 状态 1-可用 2-异常
 * @returns {Promise} 返回授权码列表
 */
export function getModelApiKeyList(params) {
  return request({
    url: '/manager/aiModelConfig/key/page',
    method: 'post',
    data: {
      modelId: params.modelId,
      pageNum: params.pageNum || 1,
      pageSize: params.pageSize || 10,
      apiKey: params.apiKey || '',
      status: params.status
    }
  });
}

/**
 * 创建模型密钥
 * @param {Object} data - 请求参数
 * @param {number|string} data.modelId - 关联的AI模型ID（必填）
 * @param {string} data.apiKey - API密钥（必填，最多100字符）
 * @param {string} [data.remark] - 备注（最多256字符）
 * @returns {Promise} 返回创建结果
 */
export function addModelApiKey(data) {
  return request({
    url: '/manager/aiModelConfig/key/create',
    method: 'post',
    data: {
      modelId: data.modelId,
      apiKeys: data.apiKeys,
      remark: data.remark || ''
    }
  });
}

/**
 * 校验模型密钥
 * @param {Object} data - 请求参数
 * @param {number|string} data.id - 密钥ID
 * @returns {Promise} 返回校验结果
 */
export function verifyModelApiKey(data) {
  return request({
    url: '/manager/aiModelConfig/key/verify',
    method: 'post',
    data: { id: data.id }
  });
}

/**
 * 删除模型密钥
 * @param {Object} data - 请求参数
 * @param {number|string} data.id - 密钥ID
 * @returns {Promise} 返回删除结果
 */
export function deleteModelApiKey(data) {
  return request({
    url: '/manager/aiModelConfig/key/delete',
    method: 'post',
    data: { id: data.id }
  });
}

/**
 * 上传模型图标
 * @param {File} file - 图标文件
 * @returns {Promise} 返回上传结果，包含图标URL
 */
export function uploadModelIcon(file) {
  const formData = new FormData();
  formData.append('file', file);
  return request({
    url: '/manager/aiModelConfig/uploadIcon',
    method: 'post',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
}
